import { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Button, Card, Grid, Snackbar, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayoutMin from "examples/LayoutContainers/DashboardLayoutMin";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function BacklogsReport() {
  const [programs, setPrograms] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [programId, setProgramId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [courseType, setCourseType] = useState("");
  const [numberOfSubjects, setNumberOfSubjects] = useState("");
  const [feeHeadId, setFeeHeadId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [feeBookLoading, setFeeBookLoading] = useState(false);
  const [feeBookPdfLoading, setFeeBookPdfLoading] = useState(false);
  const [feeBookPdfReady, setFeeBookPdfReady] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const fetchPrograms = async () => {
      if (!clientId || !collegeId) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/programs/${clientId}/${collegeId}`, {
          headers: getAuthHeaders(),
        });
        setPrograms(response.data || []);
      } catch (err) {
        setPrograms([]);
        showSnackbar("Unable to load programs.", "error");
      }
    };

    const fetchCourseTypes = async () => {
      if (!clientId || !collegeId) return;
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/exam-backlogs/course-types/${clientId}/${collegeId}`,
          { headers: getAuthHeaders() }
        );
        setCourseTypes(response.data.data || []);
      } catch (err) {
        setCourseTypes([]);
        showSnackbar("Unable to load course types.", "error");
      }
    };

    const fetchFeeHeads = async () => {
      if (!clientId || !collegeId) return;
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/exam-backlogs/supply-fee-heads/${clientId}/${collegeId}`,
          { headers: getAuthHeaders() }
        );
        setFeeHeads(response.data.data || []);
      } catch (err) {
        setFeeHeads([]);
        showSnackbar("Unable to load supply fee heads.", "error");
      }
    };

    fetchPrograms();
    fetchCourseTypes();
    fetchFeeHeads();
  }, [clientId, collegeId]);

  useEffect(() => {
    const fetchBatches = async () => {
      if (!programId || !clientId || !collegeId) {
        setBatches([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/admittedbatches/list/${clientId}/${collegeId}/${programId}`,
          { headers: getAuthHeaders() }
        );
        const normalized = (response.data || []).map((row) => ({
          admittedbatch_id: row.admittedbatch_id,
          admittedbatch_name: row.admittedbatch?.admittedbatch || "Unknown Batch",
        }));
        setBatches(normalized);
      } catch (err) {
        setBatches([]);
        showSnackbar("Unable to load admitted batches.", "error");
      }
    };

    setBatchId("");
    setRows([]);
    setFeeBookPdfReady(false);
    fetchBatches();
  }, [programId, clientId, collegeId]);

  const handleNumberOfSubjectsChange = (event) => {
    setNumberOfSubjects(event.target.value.replace(/\D/g, ""));
    setFeeBookPdfReady(false);
  };

  const handleGetReport = async () => {
    if (!programId || !batchId) {
      showSnackbar("Please select program and admitted batch.", "warning");
      return;
    }

    setLoading(true);
    setFeeBookPdfReady(false);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/exam-backlogs/report`, {
        headers: getAuthHeaders(),
        params: {
          client_id: clientId,
          college_id: collegeId,
          program_id: programId,
          admittedbatch_id: batchId,
          course_type: courseType || undefined,
          number_of_subjects: numberOfSubjects || undefined,
        },
      });
      const reportRows = response.data.data || [];
      setRows(reportRows);
      showSnackbar(
        reportRows.length
          ? `Backlogs report generated and stored. Rows: ${reportRows.length}`
          : "No backlog records found for the selected filters.",
        reportRows.length ? "success" : "info"
      );
    } catch (err) {
      setRows([]);
      showSnackbar(err.response?.data?.message || "Unable to generate backlogs report.", "error");
    } finally {
      setLoading(false);
    }
  };

  const getSelectedLabels = () => {
    const selectedProgram = programs.find(
      (program) => String(program.program_id) === String(programId)
    );
    const selectedBatch = batches.find(
      (batch) => String(batch.admittedbatch_id) === String(batchId)
    );
    const selectedCourseType = courseTypes.find(
      (type) => String(type.course_type) === String(courseType)
    );

    return {
      programName: selectedProgram?.program_name || "",
      batchName: selectedBatch?.admittedbatch_name || "",
      courseTypeLabel: selectedCourseType?.label || "All",
    };
  };

  const handleDownloadPDF = async () => {
    if (!programId || !batchId) {
      showSnackbar("Please select program and admitted batch.", "warning");
      return;
    }

    const { programName, batchName, courseTypeLabel } = getSelectedLabels();
    setPdfLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-backlogs/download-pdf`,
        {
          client_id: clientId,
          college_id: collegeId,
          program_id: programId,
          admittedbatch_id: batchId,
          course_type: courseType || undefined,
          course_type_label: courseTypeLabel,
          number_of_subjects: numberOfSubjects || undefined,
          program_name: programName,
          batch_name: batchName,
        },
        {
          headers: getAuthHeaders(),
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "BacklogsReport.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar("Backlogs report PDF downloaded.");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Unable to download PDF.", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleAddFeeBookEntries = async () => {
    if (!programId || !batchId || !feeHeadId) {
      showSnackbar("Please select program, admitted batch, and fee head.", "warning");
      return;
    }
    if (!rows.length) {
      showSnackbar("Please generate the report before adding fee book entries.", "warning");
      return;
    }

    setFeeBookLoading(true);
    setFeeBookPdfReady(false);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-backlogs/add-fee-book`,
        {
          client_id: clientId,
          college_id: collegeId,
          program_id: programId,
          admittedbatch_id: batchId,
          course_type: courseType || undefined,
          number_of_subjects: numberOfSubjects || undefined,
          fee_head_id: feeHeadId,
          created_by: user?.email || user?.user_name || user?.name || null,
        },
        { headers: getAuthHeaders() }
      );

      const result = response.data.data || {};
      setFeeBookPdfReady(Boolean(result.matched_students));
      showSnackbar(
        `Fee book entries done. Created: ${result.created || 0}, Updated: ${
          result.updated || 0
        }, Skipped: ${result.skipped || 0}.`,
        result.created || result.updated ? "success" : "info"
      );
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Unable to add fee book entries.", "error");
    } finally {
      setFeeBookLoading(false);
    }
  };

  const handleDownloadFeeBookPDF = async () => {
    if (!programId || !batchId || !feeHeadId) {
      showSnackbar("Please select program, admitted batch, and fee head.", "warning");
      return;
    }

    const { programName, batchName, courseTypeLabel } = getSelectedLabels();
    setFeeBookPdfLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-backlogs/download-fee-book-pdf`,
        {
          client_id: clientId,
          college_id: collegeId,
          program_id: programId,
          admittedbatch_id: batchId,
          course_type: courseType || undefined,
          course_type_label: courseTypeLabel,
          number_of_subjects: numberOfSubjects || undefined,
          fee_head_id: feeHeadId,
          program_name: programName,
          batch_name: batchName,
        },
        {
          headers: getAuthHeaders(),
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "FeeBookEntries.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar("Fee book entries PDF downloaded.");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Unable to download fee book PDF.", "error");
    } finally {
      setFeeBookPdfLoading(false);
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    { field: "student_rollno", headerName: "Roll No", width: 130 },
    { field: "program_name", headerName: "Program", width: 150 },
    { field: "branch_name", headerName: "Branch", width: 180 },
    {
      field: "admittedbatch",
      headerName: "Batch",
      width: 120,
      valueGetter: (params) => params.row.admittedbatch || params.row.admittedbatch_id || "",
    },
    { field: "course_code", headerName: "Course Code", width: 130 },
    { field: "course_name", headerName: "Course Name", width: 260 },
    { field: "course_type", headerName: "Type", width: 90 },
    { field: "year_number", headerName: "Year", width: 80 },
    { field: "semester_number", headerName: "Sem", width: 80 },
    { field: "exam_month", headerName: "Exam Month", width: 130 },
    { field: "status", headerName: "Status", width: 100 },
    {
      field: "total_marks",
      headerName: "Total",
      width: 100,
      valueGetter: (params) => params.row.total_marks ?? "-",
    },
  ];

  return (
    <DashboardLayoutMin>
      <MDBox pt={1} pb={3}>
        <Card sx={{ p: 2 }}>
          <MDTypography variant="h6" gutterBottom>
            Backlogs Report
          </MDTypography>

          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12} md={2.2}>
              <TextField
                label="Program"
                select
                fullWidth
                value={programId}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                onChange={(event) => {
                  setProgramId(event.target.value);
                  setBatchId("");
                }}
              >
                <option value="">Select Program</option>
                {programs.map((program) => (
                  <option key={program.program_id} value={program.program_id}>
                    {program.program_name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2.2}>
              <TextField
                label="Admitted Batch"
                select
                fullWidth
                value={batchId}
                disabled={!programId}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                onChange={(event) => setBatchId(event.target.value)}
              >
                <option value="">Select Admitted Batch</option>
                {batches.map((batch) => (
                  <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
                    {batch.admittedbatch_name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={1.8}>
              <TextField
                label="Course Type"
                select
                fullWidth
                value={courseType}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                onChange={(event) => setCourseType(event.target.value)}
              >
                <option value="">All</option>
                {courseTypes.map((type) => (
                  <option key={type.course_type} value={type.course_type}>
                    {type.label}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={1.8}>
              <TextField
                label="Number of Subjects"
                fullWidth
                value={numberOfSubjects}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                onChange={handleNumberOfSubjectsChange}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={handleGetReport}
                disabled={loading}
                sx={{ color: "#fff", minHeight: 44 }}
              >
                {loading ? "Loading..." : "Get Report"}
              </Button>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDownloadPDF}
                disabled={loading || pdfLoading || !programId || !batchId}
                sx={{ color: "#fff", minHeight: 44 }}
              >
                {pdfLoading ? "Downloading..." : "Download PDF"}
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12} md={5}>
              <TextField
                label="Supply Fee Head"
                select
                fullWidth
                value={feeHeadId}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                onChange={(event) => {
                  setFeeHeadId(event.target.value);
                  setFeeBookPdfReady(false);
                }}
              >
                <option value="">Select Supply Fee Head</option>
                {feeHeads.map((feeHead) => (
                  <option key={feeHead.fee_head_id} value={feeHead.fee_head_id}>
                    {feeHead.fee_head_name}
                    {feeHead.year_number && feeHead.semester_number
                      ? ` - Y${feeHead.year_number} S${feeHead.semester_number}`
                      : ""}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={handleAddFeeBookEntries}
                disabled={
                  feeBookLoading || feeBookPdfLoading || loading || !feeHeadId || !rows.length
                }
                sx={{ color: "#fff", minHeight: 44 }}
              >
                {feeBookLoading ? "Adding..." : "Add Entry to Fee Book"}
              </Button>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDownloadFeeBookPDF}
                disabled={feeBookPdfLoading || feeBookLoading || loading || !feeBookPdfReady}
                sx={{ color: "#fff", minHeight: 44 }}
              >
                {feeBookPdfLoading ? "Downloading..." : "Download Entries PDF"}
              </Button>
            </Grid>
          </Grid>

          {rows.length === 0 && !loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select filters and click Get Report.
            </Alert>
          )}

          <MDBox sx={{ height: 520, width: "100%" }}>
            <DataGrid
              rows={rows.map((row, index) => ({
                id: `${row.student_rollno}-${row.course_code}-${index}`,
                ...row,
              }))}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[10, 20, 50]}
              loading={loading}
              disableSelectionOnClick
            />
          </MDBox>
        </Card>
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayoutMin>
  );
}

export default BacklogsReport;
